<template>
  <section :class="classes">
    <header class="instant-answer__header">
      <slot name="header" />
    </header>
    <div
      v-if="srTitle"
      class="sr-only"
      role="heading"
      aria-level="2"
      data-test-id="instant-answer-sr-title"
    >
      {{ srTitle }}
    </div>
    <div class="instant-answer__content">
      <div v-if="$slots.context" class="instant-answer__context">
        <slot name="context" />
      </div>
      <EHeading v-if="$slots.title" class="instant-answer__title" as="div">
        <slot name="title" />
      </EHeading>
      <div v-if="$slots.subtitle" class="instant-answer__subtitle">
        <slot name="subtitle" />
      </div>
      <div v-if="$slots.default" class="instant-answer__body">
        <slot />
      </div>
    </div>
    <div class="instant-answer__groove" aria-hidden="true">
      <ETrees
        v-if="trees"
        class="instant-answer__trees"
        data-test-id="instant-answer-trees"
      />
      <EWave class="instant-answer__wave" data-test-id="instant-answer-wave" />
    </div>
    <ERichContentFooter
      v-show="showFooter"
      class="instant-answer__footer"
      as="footer"
      :sources="sources"
      :feedback-link="feedbackOptions"
      :divider="false"
      :vertical-align="footerVerticalAlign"
      :powered-by-ecosia="poweredByEcosia"
      data-test-id="instant-answer-footer"
    >
      <template #left>
        <slot name="footer-left" />
      </template>
      <template #right>
        <slot name="footer-right" />
      </template>
    </ERichContentFooter>
  </section>
</template>

<script>
import EHeading from '~~/common/vue/components/heading';
import ERichContentFooter from '~~/common/vue/components/rich-content-footer';
import classes from '~~/common/vue/mixins/classes';

export default {
  name: 'EInstantAnswer',
  components: {
    EHeading,
    ERichContentFooter,
    ETrees: async () => import(
      /* webpackPrefetch: true */
      /* webpackChunkName: "trees" */
      './trees.svg'
    ),
    EWave: async () => import(
      /* webpackPrefetch: true */
      /* webpackChunkName: "wave" */
      './wave.svg'
    ),
  },
  mixins: [classes],
  props: {
    ...ERichContentFooter.props,
    srTitle: {
      type: String,
      default: '',
    },
    showFooter: {
      type: Boolean,
      default: true,
    },
    trees: {
      type: Boolean,
      default: false,
      cssClass: true,
    },
    noPadding: {
      type: Boolean,
      default: false,
      cssClass: true,
    },
    footerVerticalAlign: ERichContentFooter.props.verticalAlign,
  },
  computed: {
    feedbackOptions() {
      return {
        errorMessage: this.$t('common.header.feedback.error'),
        options: [
          { label: this.$t('common.header.feedback.bullet1'), value: 'helpful' },
          { label: this.$t('common.header.feedback.bullet2'), value: 'missing' },
          { label: this.$t('common.header.feedback.bullet3'), value: 'wrong' },
          { label: this.$t('common.header.feedback.bullet4'), value: 'not_helpful' },
        ],
        query: this.$route ? this.$route.query?.q?.toString() : '',
        submitText: this.$t('common.header.feedback.send'),
        successMessage: this.$t('common.header.feedback.body2'),
        successTitle: this.$t('common.header.feedback.title2'),
        textareaPlaceholder: this.$t('common.header.feedback.tellmore'),
        title: this.$t('common.header.feedback.login'),
        ticketSubject: 'Search - Translation',
        url: process.env.feedbackUrl,
        ...this.feedbackLink,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.instant-answer {
  --ia-backdrop-color: var(--color-background-secondary);

  display: flex;
  flex-direction: column;
  background: var(--color-background-quaternary);

  @include desktop {
    --ia-backdrop-color: var(--color-background-primary);

    background: var(--color-background-secondary);
    border-top-left-radius: $border-radius-l;
    border-top-right-radius: $border-radius-l;
  }
}

.instant-answer__content {
  flex-grow: 1;
  padding: $space-m;

  .instant-answer--no-padding & {
    padding: 0;
  }
}

.instant-answer__context {
  margin-bottom: $space-m;
}

.instant-answer__title {
  font-size: $font-3l;
  font-weight: $font-weight-1l;
}

.instant-answer__subtitle {
  color: var(--color-text-secondary);
  font-size: $font-s;

  &:not(:empty) + .instant-answer__body {
    margin-top: $space-m;
  }
}

.instant-answer__groove {
  height: $space-1l;
  position: relative;

  .instant-answer--trees & {
    height: $space-3l;
  }
}

.instant-answer__trees,
.instant-answer__wave {
  position: absolute;
  bottom: 0;
}

.instant-answer__wave {
  color: var(--ia-backdrop-color);
  right: 0;
}

.instant-answer__trees {
  right: $space-m;

  @include desktop {
    right: $space-3l;
  }
}

.instant-answer__footer {
  background: var(--ia-backdrop-color);
  flex-wrap: wrap;
}
</style>
